import {
  fetchLinkedContactPage,
  fetchLinkNotifications,
  fetchNotices,
  markNoticesSeen,
  fetchConsultantLink as fetchConsultantLinkApi,
  fetchAcceptedConsultants,
  updateLinkedConsultant,
  updateLinkedVendor,
} from '../util/api';

/**
 * An organisation's consultant and vendor links, read a page at a time and changed one link at a
 * time. Pages are kept by scope — `own` for the signed-in user's organisation, `listing` for the
 * listing being viewed — and by list name, each as
 * `{ status: 'idle' | 'loading' | 'loaded' | 'failed', items, total, page, perPage }`.
 */

// The most accepted consultants the picker shows at once.
const PICKER_SIZE = 25;

// ================ Action types ================ //

export const LINK_PAGE_REQUEST = 'app/linkedContacts/LINK_PAGE_REQUEST';
export const LINK_PAGE_SUCCESS = 'app/linkedContacts/LINK_PAGE_SUCCESS';
export const LINK_PAGE_ERROR = 'app/linkedContacts/LINK_PAGE_ERROR';

export const NOTIFICATIONS_REQUEST = 'app/linkedContacts/NOTIFICATIONS_REQUEST';
export const NOTIFICATIONS_SUCCESS = 'app/linkedContacts/NOTIFICATIONS_SUCCESS';
export const NOTIFICATIONS_ERROR = 'app/linkedContacts/NOTIFICATIONS_ERROR';
export const NOTICES_SUCCESS = 'app/linkedContacts/NOTICES_SUCCESS';

export const ACCEPTED_CONSULTANTS_EXIST = 'app/linkedContacts/ACCEPTED_CONSULTANTS_EXIST';

export const LISTING_LINK_PAGES_CLEAR = 'app/linkedContacts/LISTING_LINK_PAGES_CLEAR';

// ================ Reducer ================ //

const emptyPage = { status: 'idle', items: [], total: 0, page: 1, perPage: 0 };

const initialState = {
  own: {},
  listing: {},
  notifications: { status: 'idle', hasUnseen: false, recruiterRequests: [], consultantAnswers: [] },
  // The bell's notices, newest first, and how many are unseen.
  notices: { items: [], unseen: 0 },
  acceptedConsultantsExist: null,
};

// Each page request carries a number; an answer is taken only while its number is still the latest
// asked for that list, so a slow earlier page never replaces the one the user moved on to.
let lastRequestId = 0;

const withPage = (state, scope, list, update) => {
  const current = state[scope][list] || emptyPage;
  const next = update(current);
  return next === current ? state : { ...state, [scope]: { ...state[scope], [list]: next } };
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case LINK_PAGE_REQUEST:
      return withPage(state, payload.scope, payload.list, page => ({
        ...page,
        status: 'loading',
        requestId: payload.requestId,
      }));
    case LINK_PAGE_SUCCESS:
      return withPage(state, payload.scope, payload.list, page =>
        page.requestId !== payload.requestId
          ? page
          : {
              status: 'loaded',
              items: payload.items,
              total: payload.total,
              page: payload.page,
              perPage: payload.perPage,
            }
      );
    case LINK_PAGE_ERROR:
      return withPage(state, payload.scope, payload.list, page =>
        page.requestId !== payload.requestId ? page : { ...page, status: 'failed' }
      );

    case NOTIFICATIONS_REQUEST:
      return { ...state, notifications: { ...state.notifications, status: 'loading' } };
    case NOTIFICATIONS_SUCCESS:
      return { ...state, notifications: { status: 'loaded', ...payload } };
    case NOTIFICATIONS_ERROR:
      return { ...state, notifications: { ...state.notifications, status: 'failed' } };
    case NOTICES_SUCCESS:
      return { ...state, notices: payload };

    case ACCEPTED_CONSULTANTS_EXIST:
      return { ...state, acceptedConsultantsExist: payload };

    // A late answer for a list of the listing left behind finds no request of its own and is dropped.
    case LISTING_LINK_PAGES_CLEAR:
      return { ...state, listing: {} };

    default:
      return state;
  }
}

// ================ Action creators ================ //

// Forgets the pages of the listing being viewed, so another listing never shows them.
export const clearListingLinkPages = () => ({ type: LISTING_LINK_PAGES_CLEAR });

// ================ Thunks ================ //

const currentUserOf = getState => getState().user?.currentUser || null;

const hasProfileListing = currentUser => {
  const publicData = currentUser?.attributes?.profile?.publicData;
  return !!(publicData?.profileListingId || publicData?.firstListingId);
};

const organisationName = currentUser =>
  currentUser?.attributes?.profile?.displayName || currentUser?.attributes?.profile?.firstName;

/**
 * Reads one page of one list. A user with no profile listing has no stored links of their own, so
 * no `own` page is fetched for them and nothing is marked in flight.
 *
 * @returns the page as stored, or null when nothing was fetched
 */
export const fetchLinkPage = ({ scope, list, ownerId, page, perPage, listingId, listingPage }) => async (
  dispatch,
  getState
) => {
  if (scope === 'own' && !hasProfileListing(currentUserOf(getState))) {
    return null;
  }
  lastRequestId += 1;
  const requestId = lastRequestId;
  const key = { scope, list, requestId };
  dispatch({ type: LINK_PAGE_REQUEST, payload: key });

  const query = { list, ownerId, page, perPage };
  if (listingId !== undefined) query.listingId = listingId;
  if (listingPage !== undefined) query.listingPage = listingPage;

  try {
    const response = await fetchLinkedContactPage(query);
    if (!response?.success || !response.data) {
      dispatch({ type: LINK_PAGE_ERROR, payload: key });
    } else {
      dispatch({ type: LINK_PAGE_SUCCESS, payload: { ...key, ...response.data } });
    }
  } catch (e) {
    dispatch({ type: LINK_PAGE_ERROR, payload: key });
  }
  return getState().linkedContacts[scope][list];
};

/**
 * Reads again the organisation's page of `list` that is on screen, at the size it was read, after
 * links on it changed. A list that has never loaded a page has nothing on screen to re-read.
 *
 * @returns the page as stored, or null when nothing was fetched
 */
export const refreshOwnLinkPage = list => async (dispatch, getState) => {
  const shown = getState().linkedContacts.own[list];
  if (!shown || !shown.perPage) {
    return null;
  }
  const ownerId = getState().user?.profileListingAdminId;
  return dispatch(fetchLinkPage({ scope: 'own', list, ownerId, page: shown.page, perPage: shown.perPage }));
};

// The bell's notices. A read that fails leaves the ones already shown.
export const fetchBellNotices = () => async (dispatch, getState) => {
  if (!currentUserOf(getState)) return;
  try {
    const answer = await fetchNotices();
    dispatch({ type: NOTICES_SUCCESS, payload: { items: answer?.items || [], unseen: answer?.unseen || 0 } });
  } catch (e) {
    // The bell keeps what it shows.
  }
};

// One notice seen, then the notices read again.
export const markNoticeSeen = id => async dispatch => {
  try {
    await markNoticesSeen([id]);
  } catch (e) {
    // Read again either way; it stays unseen when the mark did not land.
  }
  return dispatch(fetchBellNotices());
};

// The bell's link notifications: whether any is unseen, and the newest of each kind; and its notices.
export const fetchNotifications = () => async (dispatch, getState) => {
  if (!currentUserOf(getState)) return;
  const notices = dispatch(fetchBellNotices());
  dispatch({ type: NOTIFICATIONS_REQUEST });
  try {
    const response = await fetchLinkNotifications();
    if (!response?.success || !response.data) {
      dispatch({ type: NOTIFICATIONS_ERROR });
    } else {
      const { hasUnseen, recruiterRequests, consultantAnswers } = response.data;
      dispatch({
        type: NOTIFICATIONS_SUCCESS,
        payload: {
          hasUnseen: !!hasUnseen,
          recruiterRequests: recruiterRequests || [],
          consultantAnswers: consultantAnswers || [],
        },
      });
    }
  } catch (e) {
    dispatch({ type: NOTIFICATIONS_ERROR });
  }
  await notices;
};

// Whether the agency has any accepted consultant to apply with. Unknown (null) when the read fails.
export const fetchAcceptedConsultantsExist = () => async dispatch => {
  try {
    const response = await fetchAcceptedConsultants({ limit: 1 });
    const exists = response?.success ? (response.data?.items || []).length > 0 : null;
    dispatch({ type: ACCEPTED_CONSULTANTS_EXIST, payload: exists });
  } catch (e) {
    dispatch({ type: ACCEPTED_CONSULTANTS_EXIST, payload: null });
  }
};

// The agency's accepted consultants whose name contains `q`, newest first.
export const searchAcceptedConsultants = q => async () => {
  const text = typeof q === 'string' ? q.trim() : '';
  const query = text ? { q: text, limit: PICKER_SIZE } : { limit: PICKER_SIZE };
  try {
    const response = await fetchAcceptedConsultants(query);
    return response?.success ? { ok: true, items: response.data?.items || [] } : { ok: false, items: [] };
  } catch (e) {
    return { ok: false, items: [] };
  }
};

// The organisation's one staffing link to a consultant, or null when there is none.
export const fetchConsultantLink = consultantId => async () => {
  try {
    const response = await fetchConsultantLinkApi({ consultantId });
    return response?.success ? { ok: true, link: response.data?.link || null } : { ok: false, link: null };
  } catch (e) {
    return { ok: false, link: null };
  }
};

// ================ Link actions ================ //

// Every link action answers { ok, status, error } and never throws, so a caller saving several
// changes can report each one and carry on.
const answered = async send => {
  try {
    const response = await send();
    const result = { ok: true, status: 200, error: null };
    // A member's change the company's policy sends to a manager for approval is answered pending.
    if (response?.pending) return { ...result, pending: true };
    return response?.record ? { ...result, record: response.record } : result;
  } catch (e) {
    return { ok: false, status: e?.status ?? null, error: e?.error || e?.message || null };
  }
};

const updateRoute = kind => (kind === 'vendor' ? updateLinkedVendor : updateLinkedConsultant);

// `applyOnBehalf: {listingId}` names the job a supplier is applying the consultant to, so the add
// is made directly whatever the company's member policy.
const onBehalf = applyOnBehalf => (applyOnBehalf ? { applyOnBehalf } : {});

export const addLink = ({ kind, type, party, applyOnBehalf }) => async (dispatch, getState) => {
  const name = organisationName(currentUserOf(getState));
  const body =
    kind === 'vendor'
      ? { customerName: name, addRecord: { type, vendor: party } }
      : { recruiterName: name, addRecord: { type, consultant: party }, ...onBehalf(applyOnBehalf) };
  return answered(() => updateRoute(kind)(body));
};

export const removeLink = ({ kind, _id }) => async () =>
  answered(() => updateRoute(kind)({ deleteRecord: true, record: { _id } }));

export const editLinkNote = ({ kind, _id, description }) => async () =>
  answered(() => updateRoute(kind)({ editNoteRecord: { record: { _id }, description } }));

export const markLinkSeen = ({ _id }) => async () =>
  answered(() => updateLinkedConsultant({ markSeenRecord: { record: { _id } } }));

export const requestLinkAgain = ({ _id, applyOnBehalf }) => async () =>
  answered(() => updateLinkedConsultant({ requestAgainRecord: true, record: { _id }, ...onBehalf(applyOnBehalf) }));
